import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Eye, Printer, Pencil, RotateCcw } from "lucide-react";

const FIELDS = [
  ["ref_no", "Ref. No."], ["date", "Date"], ["from_name", "From (name)"], ["from_address", "From (address)", true],
  ["goods", "Item / goods description"], ["declared_value", "Declared value (Rs.)"], ["pickup", "From location"],
  ["delivery", "To location"], ["premium_pct", "Premium %"], ["amount", "Amount (Rs.)"], ["clause", "Clause No."],
];
const STATUS = {
  auto: ["Auto-filled", "bg-muted text-muted-foreground"],
  edited: ["Edited", "bg-amber-500/15 text-amber-700 dark:text-amber-400"],
  printed: ["Printed", "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"],
};

const fetchPdf = async (id, print) => {
  const r = await api.get(`/leads/${id}/documents/carrier-risk-receipt${print ? "?print=1" : ""}`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
};

export const viewReceipt = async (id) => {
  const w = window.open("", "_blank");
  try { const url = await fetchPdf(id, false); if (w) w.location.href = url; else window.open(url, "_blank"); }
  catch (e) { if (w) w.close(); toast.error(apiErr(e)); }
};

export const printReceipt = async (id) => {
  try {
    const url = await fetchPdf(id, true);
    const old = document.getElementById("crr-print-frame"); if (old) old.remove();
    const f = document.createElement("iframe");
    f.id = "crr-print-frame"; f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    f.src = url;
    f.onload = () => setTimeout(() => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(url, "_blank"); } }, 300);
    document.body.appendChild(f);
  } catch (e) { toast.error(apiErr(e)); }
};

const EditDialog = ({ lead, onClose, onSaved }) => {
  const [v, setV] = useState(null);
  const [edited, setEdited] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!lead) return;
    setV(null);
    api.get(`/leads/${lead.id}/carrier-risk`).then((r) => { setV(r.data.values); setEdited(r.data.edited_fields); }).catch((e) => toast.error(apiErr(e)));
  }, [lead]);

  const save = async (andPrint, reset) => {
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}/carrier-risk`, reset ? {} : v);
      toast.success(reset ? "Reset to booking values" : "Receipt saved");
      if (andPrint) await printReceipt(lead.id);
      onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="crr-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight">Carrier Risk Receipt · {lead?.order_number}</DialogTitle></DialogHeader>
        {!v ? <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
            {FIELDS.map(([k, label, wide]) => (
              <div key={k} className={wide ? "sm:col-span-2" : ""}>
                <Label className="text-xs">{label}{edited.includes(k) && <span className="ml-1 text-amber-600">· edited</span>}</Label>
                <Input className="rounded-none mt-1 h-9" value={v[k] || ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`crr-field-${k}`} />
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" className="rounded-none gap-1 mr-auto" onClick={() => save(false, true)} disabled={saving} data-testid="crr-reset"><RotateCcw className="h-4 w-4" /> Reset to booking</Button>
          <Button variant="outline" className="rounded-none" onClick={() => save(false)} disabled={!v || saving} data-testid="crr-save">Save</Button>
          <Button className="rounded-none gap-1 font-bold uppercase" onClick={() => save(true)} disabled={!v || saving} data-testid="crr-save-print"><Printer className="h-4 w-4" /> Save & Print</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Actions = ({ r, onEdit }) => (
  <div className="flex gap-1 justify-end">
    <Button size="sm" variant="outline" className="rounded-none h-8 gap-1 text-xs" onClick={() => onEdit(r)} data-testid={`crr-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
    <Button size="sm" variant="outline" className="rounded-none h-8 gap-1 text-xs" onClick={() => viewReceipt(r.id)} data-testid={`crr-view-${r.id}`}><Eye className="h-3.5 w-3.5" /> View</Button>
    <Button size="sm" className="rounded-none h-8 gap-1 text-xs" onClick={() => printReceipt(r.id)} data-testid={`crr-print-${r.id}`}><Printer className="h-3.5 w-3.5" /> Print</Button>
  </div>
);

const Badge = ({ s }) => {
  const [l, cls] = STATUS[s] || STATUS.auto;
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>{l}</span>;
};

export function CarrierRiskTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/carrier-risk${q ? `?search=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-3" data-testid="carrier-risk-tab">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order no, customer, phone…" className="rounded-none bg-card pl-9 h-10" data-testid="crr-search" />
      </div>
      <div className="border border-border bg-card divide-y divide-border">
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No orders found.</div>}
        {!loading && rows.map((r) => (
          <div key={r.id} className="p-3 flex flex-col md:flex-row md:items-center gap-2 hover:bg-muted/40 transition-colors" data-testid={`crr-row-${r.id}`}>
            <div className="md:w-40"><div className="font-bold font-mono-num">{r.order_number}</div><div className="text-xs text-muted-foreground">{r.date}</div></div>
            <div className="flex-1 min-w-0"><div className="font-semibold truncate">{r.customer_name}</div><div className="text-xs text-muted-foreground truncate">{r.route}</div></div>
            <div className="md:w-28"><Badge s={r.receipt_status} /></div>
            <Actions r={r} onEdit={setEditing} />
          </div>
        ))}
      </div>
      <EditDialog lead={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
